import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ComicCard, ComicCardSkeleton } from './ComicCard';

export function ComicCarousel({ 
  title, 
  subtitle,
  comics, 
  loading = false,
  viewAllLink,
  showArrows = true
}) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, [comics]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.8;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative" data-testid={`carousel-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Header - Otaku Style (Centered) */}
      <div className="text-center mb-8 page-padding">
        {subtitle && (
          <p className="text-[11px] tracking-[0.3em] text-[#E63946] uppercase mb-2">{subtitle}</p>
        )}
        <div className="flex items-center justify-center gap-8">
          {showArrows && canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 border border-[var(--border)] flex items-center justify-center hover:border-[#E63946] hover:text-[#E63946] transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-teko font-semibold uppercase tracking-wide">
            {title}
          </h2>
          
          {showArrows && canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 border border-[var(--border)] flex items-center justify-center hover:border-[#E63946] hover:text-[#E63946] transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>
        
        {viewAllLink && (
          <Link 
            to={viewAllLink} 
            className="inline-block mt-4 text-xs tracking-[0.2em] text-[var(--text-secondary)] hover:text-[#E63946] transition-colors uppercase"
          >
            View All →
          </Link>
        )}
      </div>

      {/* Scrollable Content - Two Row Grid */}
      <div
        ref={scrollRef}
        className="grid grid-rows-2 grid-flow-col auto-cols-max gap-x-4 gap-y-6 overflow-x-auto scrollbar-hide scroll-smooth px-4 md:px-8 lg:px-16 pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {loading ? (
          [...Array(12)].map((_, i) => (
            <div key={i} className="w-28 md:w-32 lg:w-36">
              <ComicCardSkeleton />
            </div>
          ))
        ) : (
          comics.map((comic) => (
            <div key={comic.id} className="w-28 md:w-32 lg:w-36">
              <ComicCard comic={comic} />
            </div>
          ))
        )}
      </div>
    </section>
  );
}

export function GenreCarousel({ genre, label, icon: Icon, iconColor }) {
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchComics() {
      try {
        const { getComics } = await import('../services/api');
        const res = await getComics({ genre, limit: 12 });
        setComics(res.data);
      } catch (error) {
        console.error(`Error fetching ${genre} comics:`, error);
      } finally {
        setLoading(false);
      }
    }
    fetchComics();
  }, [genre]);

  if (!loading && comics.length === 0) return null;

  return (
    <ComicCarousel
      title={label}
      comics={comics}
      loading={loading}
      viewAllLink={`/browse?genre=${genre}`}
    />
  );
}

export default ComicCarousel;
